import DateTime from '@/modules/utils/HDateTime';
import WebFontLoader from "webfontloader";
import { agreementBranding, brandingFontFamilies } from '@moxie/branding';
import CustomFontService from '@/modules/utils/CustomFontService';
import countries from '@/assets/data/countries-simple.json';

const DATE_TOKEN_FORMATS = [
	['Date.MMDDYYYY', 'MM/dd/yyyy'],
	['Date.MMMDDYYYY', 'MMM dd, yyyy'],
	['Date.MMMMDDYYYY', 'MMMM dd, yyyy'],
	['Date.DDMMYYYY', 'dd/MM/yyyy'],
	['Date.DDMMMYYYY', 'dd MMM yyyy'],
	['Date.DDMMMMYYYY', 'dd MMMM yyyy'],
	['Date.YYYYMMDD', 'yyyy-MM-dd'],
	['Date.Month', 'MMMM'],
	['Date.Year', 'yyyy'],
	['Date.24HR', 'HH:mm'],
	['Date.12HR', 'h:mm a'],
];
import { allocatePayment, allocateTaxAmounts, calculateTaxAmount, describeTaxComponents, effectiveRate } from '@/modules/agreements/taxMath';

export default {
	data: function() {
		return {
			timeUnits: [
				{ text: 'Days', value: 'DAYS' },
				{ text: 'Weeks', value: 'WEEKS' },
				{ text: 'Months', value: 'MONTHS' },
			],
		};
	},

	methods: {
		loadFonts: async function(agreement){
			let fonts = new Set();

			fonts.add(agreement.styles.body.fontFamily);
			fonts.add(agreement.styles.h1.fontFamily);
			fonts.add(agreement.styles.h2.fontFamily);
			fonts.add(agreement.styles.h3.fontFamily);
			const branding = agreementBranding(agreement);
			if (branding) fonts = new Set(brandingFontFamilies(branding));
			if (agreement.styles.h4 && agreement.styles.h4.fontFamily) fonts.add(agreement.styles.h4.fontFamily);
			let customFonts = [];
			try {
				const response = await new CustomFontService(this.$store.state.domain.podUrl).getCustomFonts(agreement.accountId);
				customFonts = Array.isArray(response.data) ? response.data : [];
			} catch (_) {
				// Without the catalog, custom names must not be sent to Google.
				return;
			}
			customFonts.filter(font => fonts.has(font.fontName)).forEach(font => {
				fonts.delete(font.fontName);
				new FontFace(font.fontName, `url(${font.fontUrl})`).load().then(loaded => document.fonts.add(loaded)).catch(() => {});
			});
			fonts.forEach(font => {
				WebFontLoader.load({ google: { families: [`${font}:100,200,300,400,500,600,700,800`] } });
			});
		},

		checkNull: function(val) {
			return val ? val : '';
		},

		getFullCountry(isoCode){
			let country = countries.find(c => c.code === isoCode);
			if(country){
				return country.name;
			}else{
				return isoCode;
			}
		},

		getTokensFromAgreement: function(agreement) {
			let result = new Map();

			if (agreement) {
				// Signed agreements resolve custom-field tokens against the definitions frozen at signing.
				const clientFields = agreement.renderContext?.clientFields ?? this.clientFields;
				const pipelineFields = agreement.renderContext?.pipelineFields ?? this.pipelineFields;
				result.set('Agreement.Name',agreement.name)
				result.set('Agreement.DateCreated',DateTime.fromISO(agreement.dateCreated).toLocaleString(DateTime.DATE_MED));
				result.set('Agreement.DateCreatedFull',DateTime.fromISO(agreement.dateCreated).toLocaleString(DateTime.DATE_FULL));

				if(agreement.dateCompleted) {
					result.set('Agreement.DateSigned', DateTime.fromISO(agreement.dateCompleted).toLocaleString(DateTime.DATE_MED));
					result.set('Agreement.DateSignedFull', DateTime.fromISO(agreement.dateCompleted).toLocaleString(DateTime.DATETIME_FULL));
				}else{
					result.set('Agreement.DateSigned',DateTime.now().toLocaleString(DateTime.DATE_MED));
					result.set('Agreement.DateSignedFull',DateTime.now().toLocaleString(DateTime.DATE_FULL));
				}

				if(agreement.settings.expirationDate){
					result.set('Agreement.ExpirationDate',DateTime.fromISO(agreement.settings.expirationDate).toLocaleString(DateTime.DATE_MED));
					result.set('Agreement.ExpirationDateFull',DateTime.fromISO(agreement.settings.expirationDate).toLocaleString(DateTime.DATE_FULL));
				}else{
					result.set('Agreement.ExpirationDate','[Exp Date]');
					result.set('Agreement.ExpirationDateFull','[Exp Date]');
				}
				result.set('Agreement.TaxDetails', this.getTaxDescription(agreement));

				// Today until fully executed, then the execution date (sealed with the agreement).
				// Formats mirror the server TokenService Date.* tokens.
				const date = agreement.dateCompleted ? DateTime.fromISO(agreement.dateCompleted) : DateTime.now();
				DATE_TOKEN_FORMATS.forEach(([token, format]) => result.set(token, date.toFormat(format)));

				result.set('Account.Name', this.checkNull(agreement.account.accountName));
				result.set('Account.TaxId', this.checkNull(agreement.account.taxId));
				result.set('Account.Address1', this.checkNull(agreement.account.address1));
				result.set('Account.Address2', this.checkNull(agreement.account.address2));
				result.set('Account.City', this.checkNull(agreement.account.city));
				result.set('Account.Locality', this.checkNull(agreement.account.locality));
				result.set('Account.Postal', this.checkNull(agreement.account.postal));
				result.set('Account.Country', this.checkNull(agreement.account.country));
				result.set('Account.CountryName', this.getFullCountry(agreement.account.country));
				result.set('Account.Email', this.checkNull(agreement.account.businessEmail));
				result.set('Account.Phone', this.checkNull(agreement.account.phone));
				result.set('Account.Website', this.checkNull(agreement.account.website));

				result.set('Client.Name', this.checkNull(agreement.client?.name));
				result.set('Client.Initials', this.checkNull(agreement.client?.initials));
				result.set('Client.TaxId', this.checkNull(agreement.client?.taxId));
				result.set('Client.Address1', this.checkNull(agreement.client?.address1));
				result.set('Client.Address2', this.checkNull(agreement.client?.address2));
				result.set('Client.City', this.checkNull(agreement.client?.city));
				result.set('Client.Locality', this.checkNull(agreement.client?.locality));
				result.set('Client.Postal', this.checkNull(agreement.client?.postal));
				result.set('Client.Country', this.checkNull(agreement.client?.country));
				result.set('Client.CountryName', this.getFullCountry(agreement.client?.country));
				result.set('Client.Phone', this.checkNull(agreement.client?.phone));
				result.set('Client.Website', this.checkNull(agreement.client?.website));
				result.set('Client.TaxRate',this.getTaxRate(agreement));
				result.set('Client.HourlyRate',this.$formatters.dollars(agreement.client?.hourlyAmount, true, true, agreement.currency));

				clientFields.forEach((f) => {
					let value = agreement.client?.customValues.find((v) => v.mappingKey === f.mappingKey);
					if(f.type === 'Date' && value && value.value){
						result.set('Client.Custom.' + f.mappingKey, DateTime.fromISO(value.value).toLocaleString(DateTime.DATE_FULL));
					}else {
						result.set('Client.Custom.' + f.mappingKey, value ? value.value : '');
					}
				});

				result.set('Opportunity.Name',this.checkNull(agreement.opportunity?.name));
				result.set('Opportunity.Value', agreement.opportunity?.value != null ? this.$formatters.dollars(agreement.opportunity.value, true, true, agreement.account.currency || agreement.currency) : '');
				result.set('Opportunity.Description',this.checkNull(agreement.opportunity?.description));
				result.set('Opportunity.FirstName',this.checkNull(agreement.opportunity?.formData?.firstName));
				result.set('Opportunity.LastName',this.checkNull(agreement.opportunity?.formData?.lastName));
				result.set(
					'Opportunity.FullName',
					[agreement.opportunity?.formData?.firstName, agreement.opportunity?.formData?.lastName]
						.filter(Boolean)
						.join(' ')
				);
				result.set('Opportunity.Email',this.checkNull(agreement.opportunity?.formData?.email));
				result.set('Opportunity.Phone',this.checkNull(agreement.opportunity?.formData?.phone));
				result.set('Opportunity.Role',this.checkNull(agreement.opportunity?.formData?.role));
				result.set('Opportunity.BusinessName',this.checkNull(agreement.opportunity?.formData?.businessName));
				result.set('Opportunity.Website',this.checkNull(agreement.opportunity?.formData?.website));
				result.set('Opportunity.Address1',this.checkNull(agreement.opportunity?.formData?.address1));
				result.set('Opportunity.Address2',this.checkNull(agreement.opportunity?.formData?.address2));
				result.set('Opportunity.City',this.checkNull(agreement.opportunity?.formData?.city));
				result.set('Opportunity.Locality',this.checkNull(agreement.opportunity?.formData?.locality));
				result.set('Opportunity.Postal',this.checkNull(agreement.opportunity?.formData?.postal));
				result.set('Opportunity.Country',this.checkNull(agreement.opportunity?.formData?.country));
				result.set('Opportunity.CountryName',this.getFullCountry(agreement.opportunity?.formData?.country));

				pipelineFields.forEach((f) => {
					let value = agreement.opportunity?.customValues.find((v) => v.mappingKey === f.mappingKey);

					if(f.type === 'Date' && value && value.value){
						result.set('Opportunity.Custom.' + f.mappingKey, DateTime.fromISO(value.value).toLocaleString(DateTime.DATE_FULL));
					}else {
						result.set('Opportunity.Custom.' + f.mappingKey, value ? value.value : '');
					}
				});

				agreement.signers
					.filter((s) => s.signerType === 'CLIENT')
					.forEach((s) => {
						result.set('Signer.FirstName', this.checkNull(s.firstName));
						result.set('Signer.LastName', this.checkNull(s.lastName));
						result.set('Signer.Email', this.checkNull(s.email));
						result.set('Signer.Title', this.checkNull(s.title));
					});

				let paymentPlan = this.getPaymentPlan(agreement);

				let groupedRecurring = this.getGroupedRecurring(agreement);

				if(paymentPlan){
					let firstPayment = paymentPlan.scheduledPayments[0];
					let firstPaymentAmount = Number(firstPayment?.calculatedAmount || 0);
					let firstPaymentTotals = allocatePayment(firstPaymentAmount, this.getTotalUpFront(agreement, false));
					let recurringInvestment = this.getRecurringInvestment(agreement,true);
					let total = firstPaymentAmount + Number(recurringInvestment.totalPreTax) + Number(recurringInvestment.totalTax);
					result.set('UpFront.Tax', this.$formatters.dollars(firstPaymentTotals.totalTax + Number(recurringInvestment.totalTax), true, true, agreement.currency));
					result.set('UpFront.TotalWithoutTax', this.$formatters.dollars(firstPaymentTotals.totalPreTax + Number(recurringInvestment.totalPreTax), true, true, agreement.currency));
					result.set('UpFront.Total', this.$formatters.dollars(total, true, true, agreement.currency));
					result.set('UpFront.UnformattedTotal',total);
				}else {
					let totalUpFront = this.getTotalUpFront(agreement);
					result.set('UpFront.Tax', this.$formatters.dollars(totalUpFront.totalTax, true, true, agreement.currency));
					result.set('UpFront.TotalWithoutTax', this.$formatters.dollars(totalUpFront.totalPreTax, true, true, agreement.currency));
					result.set('UpFront.Total', this.$formatters.dollars(totalUpFront.totalPreTax + totalUpFront.totalTax, true, true, agreement.currency));
					result.set('UpFront.UnformattedTotal',totalUpFront.totalPreTax + totalUpFront.totalTax);
				}

				let cardProcessingFees = result.get('UpFront.UnformattedTotal');

				if (agreement.client?.whoPaysCardFees === 'Client') {
					cardProcessingFees = this.calculateAdditionalFee(agreement.account.baseCardFeeRate,cardProcessingFees);
				} else if (agreement.client?.whoPaysCardFees === 'Split') {
					cardProcessingFees = this.calculateAdditionalFee(agreement.account.splitCardFeeRate,cardProcessingFees);
				}else{
					cardProcessingFees = 0;
				}

				result.set('UpFront.CardProcessingFees', this.$formatters.dollars(cardProcessingFees, true, true, agreement.currency));

				let oneTimeTotal = this.getTotalUpFront(agreement,false);
				result.set('OneTime.Total', this.$formatters.dollars(oneTimeTotal.totalPreTax, true, true, agreement.currency));
				result.set('OneTime.TotalWithTax', this.$formatters.dollars(Number(oneTimeTotal.totalPreTax) + Number(oneTimeTotal.totalTax), true, true, agreement.currency));
				result.set('OneTime.Tax', this.$formatters.dollars(oneTimeTotal.totalTax, true, true, agreement.currency));

				let recurringTotal = 0;
				let recurringTaxAmount =0;

				groupedRecurring.forEach((value) => {
					// result.set(`Recurring.${key}.Total`,this.$formatters.dollars(value.total,true,true,agreement.currency));
					// result.set(`Recurring.${key}.TotalWithTax`,this.$formatters.dollars(value.taxAmount + value.total,true,true,agreement.currency));
					// result.set(`Recurring.${key}.Tax`,this.$formatters.dollars(value.taxAmount,true,true,agreement.currency));
					recurringTotal = recurringTotal + value.total;
					recurringTaxAmount = recurringTaxAmount + value.taxAmount;
				})

				result.set(`Recurring.Total`,this.$formatters.dollars(recurringTotal,true,true,agreement.currency));
				result.set(`Recurring.TotalWithTax`,this.$formatters.dollars(recurringTaxAmount + recurringTotal,true,true,agreement.currency));
				result.set(`Recurring.Tax`,this.$formatters.dollars(recurringTaxAmount,true,true,agreement.currency));
			}

			return result;
		},

		calculateAdditionalFee: function(feeDecimal, netAmount) {
			let totalInvoice = netAmount / (1 - feeDecimal);
			return totalInvoice - netAmount;
		},

		formatServiceItemDate: function(item) {
			if (item.relativeDueDate && item.dueDateRelative) {
				let timeUnit = this.timeUnits.find((u) => u.value === item.dueDateRelative.timeUnit);
				if (timeUnit && item.dueDateRelative.duration) {
					return item.dueDateRelative.duration + ' ' + timeUnit.text;
				} else {
					return '--';
				}
			} else if (item.dueDate) {
				return DateTime.fromISO(item.dueDate).toLocaleString(DateTime.DATE_FULL);
			} else {
				return '--';
			}
		},

		formatPackageFeeType: function(servicePackage) {
			if (servicePackage.fees.feeType === 'Retainer') {
				return servicePackage.fees.retainerSchedule + ' recurring';
			} else {
				return servicePackage.fees.feeType;
			}
		},

		formatServiceItemTotal: function(agreement, servicePackage, item) {
			if (item.addOnItem) {
				if (servicePackage.fees.feeType === 'Retainer' && !item.oneTimeFee) {
					return (
						this.$formatters.dollars(item.quantity * item.rate, true, true, agreement.currency) +
						'/' +
						servicePackage.fees.retainerSchedule.toLowerCase()
					);
				} else {
					return this.$formatters.dollars(item.quantity * item.rate, true, true, agreement.currency);
				}
			} else {
				if (servicePackage.fees.feeType === 'Per Item') {
					return `${this.$formatters.dollars(item.quantity * item.rate, true, true, agreement.currency)}`;
				} else {
					return '--';
				}
			}
		},

		getItemRate: function(item) {
			return item.quantity * item.rate;
		},

		getPackageTotal: function(agreement, servicePackage, taxableTotal = false) {
			if (servicePackage.fees.feeType !== 'Per Item') {
				let total = Number(0);

				if(!taxableTotal || servicePackage.fees.taxable){
					total = Number(servicePackage.fees.amount)
				}

				if(servicePackage.fees.feeType === 'Fixed Price'){
					servicePackage.items.forEach((i) => {
						if((i.addOnItem && i.optional && i.selected) || (i.addOnItem && !i.optional)){
							if(!taxableTotal || i.taxable) {
								total = total + (i.quantity * i.rate);
							}
						}
					});
				}else if(servicePackage.fees.feeType === 'Retainer'){
					servicePackage.items.forEach((i) => {
						if(i.oneTimeFee || !i.addOnItem){
							//do nothing
						}else if((i.addOnItem && i.optional && i.selected) || (i.addOnItem && !i.optional)){
							if(!taxableTotal || i.taxable) {
								total = total + (i.quantity * i.rate);
							}
						}
					});
				}
				return total;
			} else {
				let total = 0;
				servicePackage.items.forEach((i) => {
					if((i.addOnItem && i.optional && i.selected) || !i.addOnItem || !i.optional){
						if(!taxableTotal || i.taxable) {
							total = total + i.quantity * i.rate;
						}
					}
				});
				return total;
			}
		},

		getPackageTotalPreTax: function(agreement, servicePackage){
			return this.getPackageTotal(agreement,servicePackage,false);
		},

		getPackageTotalWithTax: function(agreement, servicePackage) {
			return this.getPackageTotal(agreement, servicePackage, false) + this.getPackageTax(agreement, servicePackage);
		},

		getPackageTax(agreement, servicePackage) {
			const packages = this.getSelectedPackages(agreement);
			const packageIndex = packages.findIndex(candidate => candidate === servicePackage || (servicePackage.id != null && candidate.id === servicePackage.id));
			if (packageIndex < 0) return this.getTaxAmount(agreement, this.getPackageTotal(agreement, servicePackage, true));
			return allocateTaxAmounts(
				agreement.taxRule,
				packages.map(candidate => this.getPackageTotal(agreement, candidate, true)),
				this.getTaxRate(agreement)
			)[packageIndex];
		},

		getFeeLabel: function(servicePackage) {
			if (servicePackage.fees.feeType === 'Retainer') {
				return servicePackage.fees.retainerSchedule;
			} else {
				return servicePackage.fees.feeType;
			}
		},

		getCurrency: function(agreement) {
			if (agreement) {
				return agreement.currency;
			} else {
				return '';
			}
		},

		getTaxLabel: function(agreement) {
			if (agreement) {
				return agreement.account.taxLabel;
			} else {
				return '';
			}
		},

		getTaxRate: function(agreement) {
			if (agreement) {
				if (agreement.taxRule?.components?.length) {
					return this.getEffectiveTaxRate(agreement.taxRule.components);
				}
				if (agreement.taxPercentage !== null && agreement.taxPercentage !== undefined) {
					return Number(agreement.taxPercentage);
				}
				return Number((agreement.client?.defaultTaxRate || agreement.client?.defaultTaxRate === 0) ? agreement.client?.defaultTaxRate : agreement.account.defaultTaxRate);
			} else {
				return 0;
			}
		},

		getEffectiveTaxRate: function(components) {
			return effectiveRate(components);
		},

		getTaxAmount: function(agreement, taxableAmount) {
			const amount = Number(taxableAmount || 0);
			if (!agreement?.taxRule?.components?.length) {
				return amount * (this.getTaxRate(agreement) / 100);
			}
			return calculateTaxAmount(agreement.taxRule, amount);
		},

		getTaxDescription: function(agreement) {
			if (agreement?.taxRule?.components?.length) {
				return describeTaxComponents(agreement.taxRule.components);
			}
			return `${this.getTaxRate(agreement)}% ${this.getTaxLabel(agreement) || 'Tax'}`;
		},

		getHourlyDepositAmount: function(servicePackage){
			let hours;
			let min = servicePackage.fees.estimateMin;
			let max = servicePackage.fees.estimateMax;

			if(min && max){
				hours = (min + max) / 2;
			}else{
				hours = min ? min : max;
			}

			return ((hours * servicePackage.fees.amount) * (servicePackage.depositAmount / 100));
		},

		getPerItemDepositAmount: function(servicePackage){
			if(servicePackage && servicePackage.items) {
				let itemTotal = 0;
				servicePackage.items.filter(i => !i.optional || (i.optional && i.selected)).forEach((i) => {
					itemTotal = itemTotal + (i.quantity * i.rate);
				});
				return (itemTotal) * (servicePackage.depositAmount / 100);
			}else{
				return 0;
			}
		},

		getDepositAmount: function(servicePackage){
			if(servicePackage.fees.feeType === 'Hourly') {
				return this.$formatters.dollars(this.getHourlyDepositAmount(servicePackage), true, true, this.agreement.currency);
			}else if(servicePackage.fees.feeType === 'Per Item'){
				return this.$formatters.dollars(this.getPerItemDepositAmount(this.element), true, true, this.agreement.currency);
			}else{
				return this.$formatters.dollars(0, true, true, this.agreement.currency);
			}
		},

		getRecurringInvestment: function(agreement, advancedOnly=false){
			let selectedPackages = this.getSelectedPackages(agreement);
			let taxableTotal = 0;

			let totals = {
				totalPreTax: 0,
				totalTax: 0
			}

			for(let i=0; i < selectedPackages.length; i++) {
				let selectedPackage = selectedPackages[i];
				let total = 0;

				if (selectedPackage.fees.feeType === 'Retainer') {
					if(selectedPackage.fees.retainerTiming === 'Arrears' && advancedOnly){
						continue;
					}
					total = selectedPackage.fees.amount;
					if (selectedPackage.fees.taxable) taxableTotal += total;

					selectedPackage.items.filter(i => i.addOnItem && ((i.optional && i.selected) || !i.optional) && !i.oneTimeFee).forEach(i => {
						total = total + (i.quantity * i.rate);
						if (i.taxable) taxableTotal += i.quantity * i.rate;
					})

					totals.totalPreTax = totals.totalPreTax + total;
				}
			}
			totals.totalTax = this.getTaxAmount(agreement, taxableTotal);

			return totals;
		},

		getTotalUpFront: function(agreement, includeRetainer = true) {
			let selectedPackages = this.getSelectedPackages(agreement);
			let taxableTotal = 0;

			let totals = {
				totalPreTax: 0,
				totalTax: 0
			}

			for(let i=0; i < selectedPackages.length; i++){
				let selectedPackage = selectedPackages[i];
				let total = 0;

				if(selectedPackage.fees.feeType === 'Hourly'){
					total = this.getHourlyDepositAmount(selectedPackage);
					if (selectedPackage.fees.taxable) taxableTotal += total;
				}else if(includeRetainer && selectedPackage.fees.feeType === 'Retainer' && selectedPackage.fees.retainerTiming === 'Advanced'){
					total = selectedPackage.fees.amount;
					if (selectedPackage.fees.taxable) taxableTotal += total;
				}else if(selectedPackage.fees.feeType === 'Per Item'){
					selectedPackage.items.forEach((i) => {
						if((i.addOnItem && i.optional && i.selected) || !i.addOnItem || !i.optional){
							total = total + (i.quantity * i.rate);
							if (i.taxable) taxableTotal += i.quantity * i.rate;
						}
					});
				}else if(selectedPackage.fees.feeType === 'Fixed Price'){
					total = Number(selectedPackage.fees.amount);
					if (selectedPackage.fees.taxable) taxableTotal += total;
				}

				if(selectedPackage.fees.feeType !== 'Per Item') {
					selectedPackage.items.filter(i => (i.addOnItem || i.oneTimeFee) && ((i.optional && i.selected) || !i.optional)).forEach(i => {
						if ((selectedPackage.fees.feeType === 'Retainer' && includeRetainer && !i.oneTimeFee) || i.oneTimeFee) {
							total = total + (i.quantity * i.rate);
							if (i.taxable) taxableTotal += i.quantity * i.rate;
						}
					})
				}

				totals.totalPreTax = Number(totals.totalPreTax) + Number(total);
			}
			totals.totalTax = this.getTaxAmount(agreement, taxableTotal);

			return totals;
		},


		getGroupedRecurring: function(agreement) {
			let selectedPackages = this.getSelectedPackages(agreement);
			let grouping = new Map();
			let schedulers = ['Weekly', 'Bi-Weekly', 'Monthly', 'Quarterly', 'Bi-Annually', 'Annually'];

			for(let i=0; i < schedulers.length; i++){
				grouping.set(schedulers[i],{taxAmount:0,total:0,taxableTotal:0})
			}

			for(let i=0; i < selectedPackages.length; i++){
				let selectedPackage = selectedPackages[i];
				if (selectedPackage.fees.feeType !== 'Retainer') continue;
				let total = 0;

				total = Number(selectedPackage.fees.amount);

				selectedPackage.items.filter(i => i.addOnItem && (i.optional ? i.selected : true) && !i.oneTimeFee).forEach(i => {
					total = total + (i.quantity * i.rate);
				})

				let result = grouping.get(selectedPackage.fees.retainerSchedule);
				if(result){
					result.total = result.total + total;
					if (selectedPackage.fees.taxable) result.taxableTotal += Number(selectedPackage.fees.amount);
					selectedPackage.items
						.filter(i => i.addOnItem && (i.optional ? i.selected : true) && !i.oneTimeFee && i.taxable)
						.forEach(i => { result.taxableTotal += i.quantity * i.rate; });
				}
			}
			grouping.forEach(result => {
				result.taxAmount = this.getTaxAmount(agreement, result.taxableTotal);
				delete result.taxableTotal;
			});

			return grouping;
		},

		getSelectedPackages: function(agreement) {
			let services = this.getServices(agreement);
			let selectedPackages = [];

			for (let i = 0; i < services.length; i++) {
				let service = services[i];

				if (!service.packages.length) {
					continue;
				}

				if (service.selectionRequired && service.packages.length === 1) {
					selectedPackages.push(service.packages[0]);
				} else {
					selectedPackages.push(...service.packages.filter((p) => p && p.selected));
				}
			}

			return selectedPackages;
		},

		getServices: function(agreement) {
			return this.getItemsOfType(agreement,'Services');
		},

		getPaymentPlan: function(agreement){
			let paymentPlans = this.getItemsOfType(agreement,'PaymentPlan');
			if(paymentPlans.length){
				return paymentPlans[0];
			}else{
				return null;
			}
		},

		getItemsOfType: function(agreement,type){
			let result = [];

			for (let i = 0; i < agreement.pages.length; i++) {
				let page = agreement.pages[i];
				page.schema.forEach((item) => {
					if (item.type === type) {
						result.push(item);
					} else if (item.type === 'Container') {
						for (let j = 0; j < item.columnCount; j++) {
							item.columns[j].items.forEach((containerItem) => {
								if (containerItem.type === type) {
									result.push(containerItem);
								}
							});
						}
					}
				});
			}

			return result;
		}
	},

	computed: {
		clientFields: function() {
			if (this.$store.state.clientFields && this.$store.state.clientFields.fields) {
				return this.$store.state.clientFields.fields;
			} else {
				return [];
			}
		},

		pipelineFields: function() {
			if (this.$store.state.pipelineFields && this.$store.state.pipelineFields.fields) {
				return this.$store.state.pipelineFields.fields;
			} else {
				return [];
			}
		},

		contactFields: function() {
			if (this.$store.state.contactFields && this.$store.state.contactFields.fields) {
				return this.$store.state.contactFields.fields;
			} else {
				return [];
			}
		},
	},
};
